<template>
  <div
    class="grid-string grid-string-data"
    draggable="true"
    :style="rowStyle"
    @dragover.prevent="handleDrag"
  >
    <template v-for="field in fields">
      <component
        :is="field.component"
        :key="field.slug + field.upd"
        :editable="editable && !field.row.readonly"
        v-bind="field.props"
        v-on="field.events"
        @contextmenu.native="emitContext($event, field)"
      />
    </template>
  </div>
</template>

<script>
  import { mapActions } from 'vuex';
  import IconsCell from '@/components/project/list/IconsCell';
  import DescriptionCell from '@/components/common/DescriptionCell';
  import TextCell from '@/components/common/TextCell';
  import NumberCell from '@/components/common/NumberCell';
  import UnitDropdown from '@/components/project/list/UnitDropdown';
  import RowContext from '@/components/project/list/contexts/Row';
  import copy from '@/utils/copy';
  import factory from '@/utils/api/factory';
  import parseError from '@/utils/formatting/parse-error';
  import CommentInput from '@/components/comments/CommentInput';
  import RowMixin from '@/components/common/RowMixin';
  import ContractorCell from '@/components/common/ContractorCell';
  import getCellId from '@/utils/get-table-cell-id';
  import CommentsCell from '@/components/common/CommentsCell';
  import TenderPositionCard from '@/containers/tender/position/TenderPositionCard';

  const COUNT_OF_DEFAULT_CONTEXT_ACTIONS = 4;

  const FIELDS_DEFAULT_ZERO_VALUE = ['quantity', 'labour', 'materials', 'tools'];

  /**
   * Fast go to:
   * – {@link index_column} – first column with actions in form of icons
   * – {@link description_column} – powerful description cell
   * – {@link text_column} – text cell is for the custom columns
   * – {@link number_column} – numbers contain all calculations of the table
   * – {@link unit_column} – unit – dropdown select with possible types of units
   */

  export default {
    name: 'ListRow',
    mixins: [RowMixin],

    props: {
      editable: {
        type: Boolean,
        required: true,
      },
      rowId: {
        type: Number,
        required: true,
      },
      currency: {
        type: String,
        required: true,
      },
      inGroup: {
        type: Boolean,
        default: false,
      },
      show: {
        type: Boolean,
        default: true,
      },
      listTitle: String,
    },
    data() {
      return {
        hasBreakdown: false,
        unreadComments: this.row.unreadComments,
        commentsCounter: null,
        commentsResolved: this.row.commentsResolved,

        COMPONENTS: {
          /** @name index_column */
          index: {
            component: IconsCell,
            props: () => ({
              id: this.row.id,
              index: this.row.index,
              noButtons: !this.editable,
            }),
            events: () => ({
              add: () => this.$emit('add', this.row.order),
              startDrag: () => this.$emit('start-drag'),
              endDrag: () => this.$emit('drop'),
            }),
          },

          /** @name description_column */
          description: {
            component: DescriptionCell,
            props: (field) => ({
              class: 'cell-description',
              description: this.data[field.slug],
              rowId: this.data.id,
              error: this.errors[field.slug],
              id: getCellId({
                isBreakdown: this.hasBreakdown,
                field: field.slug,
                rowId: this.rowId,
              }),
              inGroup: this.inGroup,
              index: this.row.groupNo,
            }),
            events: (field) => ({
              change: ({ value, oldVal }) => this.changeField(value, field.slug, oldVal),
              'change-focused-cell': ({ e, value, id }) =>
                this.$emit('change-focused-cell', { e, field: field.slug, value, id }),
              update: () => {
                this.$emit('update');
              },
              options: (event) => this.emitContext(event, field),
              'open-card': () => this.openComments(),
            }),
          },

          /** @name text_column */
          text: {
            component: TextCell,
            props: (field) => {
              return {
                label: this.data[field.slug] || this.data.custom_fields?.[field.slug],
                error: this.errors[field.slug],
              };
            },
            events: (field) => ({
              change: ({ value, oldVal }) => this.changeField(value, field.slug, oldVal),
            }),
          },

          /** @name number_column */
          number: {
            component: NumberCell,
            props: (field) => ({
              currency: this.currency,
              label: this.data[field.slug] || this.data.custom_fields?.[field.slug],
              noCurrency: field.row.noCurrency,
              error: this.errors[field.slug],
              id: getCellId({
                isBreakdown: this.hasBreakdown,
                field: field.slug,
                rowId: this.rowId,
              }),
            }),
            events: (field) => ({
              change: ({ value, oldVal }) => this.changeField(value, field.slug, oldVal),
              'change-focused-cell': ({ e, value, id }) =>
                this.$emit('change-focused-cell', { e, field: field.slug, value, id }),
            }),
          },

          /** @name unit_column */
          unit: {
            component: UnitDropdown,
            props: (field) => ({
              class: 'cell-unit-dropdown',
              val: this.data[field.slug],
              error: this.errors[field.slug],
            }),
            events: (field) => ({
              change: (unit) => this.changeField(unit.id, field.slug),
            }),
          },

          /** @name contractor_column */
          contractor: {
            component: ContractorCell,
            props: (field) => ({
              listId: this.data.list_id,
              rowId: this.data.id,
              contractor: this.data[field.slug] || {},
              priceSources: this.data.tender_price_sources,
            }),
            events: () => ({}),
          },
          /** @name comments_column */
          comments: {
            component: CommentsCell,
            props: () => ({
              comments: this.comments,
              unreadComments: this.unreadComments,
              commentsResolved: this.commentsResolved,
              rowId: this.data.id,
              lastComment: this.row.last_comment,
              row: this.row,
            }),
            events: () => ({
              counter: (count) => this.countComments(count),
              resolver: (isResolved) => {
                this.commentsResolved = isResolved;
              },
              'open-card': () => this.openComments(),
            }),
          },
        },
      };
    },

    computed: {
      comments() {
        return this.commentsCounter || this.row?.comments?.aggregate.count;
      },
      rowStyle() {
        return { display: this.show ? 'contents' : 'none!important' };
      },
    },

    methods: {
      handleDrag() {
        this.$emit('drag');
      },

      ...mapActions({
        openContext: 'context/open',
        openModal: 'modal/openModal',
      }),

      emitContext(event, field) {
        if (this.editable) this.rowOptions(event, field);
      },

      async changeField(val, field, oldVal = null) {
        try {
          // По умолчанию при очистке поля ставить ноль
          if (FIELDS_DEFAULT_ZERO_VALUE.includes(field) && !val) {
            val = 0;
          }
          this.errors = { ...this.errors, [field]: '' };
          if (oldVal) this.data[field] = val;
          const res = await factory('rows').updateInPath(this.data.id, {
            field: field,
            value: val,
          });

          this.data = {
            ...this.data,
            ...res.data,
          };

          this.$emit('update', this.data);

          this.commentsCounter = res.data.comments_counter || this.comments;
        } catch (e) {
          this.errors = { ...this.errors, [field]: parseError(e) };
          if (oldVal) this.data[field] = oldVal;
        }
      },

      countComments(count) {
        this.unreadComments = 0;
        this.commentsCounter = count;
      },

      rowOptions(event, field) {
        event.preventDefault();
        const readonly = field.row.readonly;
        const icons = field.row.type === 'index';

        this.openContext({
          // Default props to context
          pageX: event.pageX,
          pageY: event.pageY,
          type: event.type,
          options: COUNT_OF_DEFAULT_CONTEXT_ACTIONS,
          optionsHeight: 28,
          component: RowContext,

          // Custom props to context
          allowPaste: !readonly && !icons && this.editable,
          allowCopy: !icons,
          allowComment: !readonly && !icons,
          rowLevel: this.data.group_level,

          // Custom "events" as props to context
          delete: () => this.$emit('delete', this.data.id),
          add: () => this.$emit('add', this.data.order),
          addGroup: (level) => this.$emit('addGroup', this.data.order, level),
          convertToGroup: (level) => this.$emit('convertToGroup', this.data.id, level),
          copy: () => copy(this.data?.[field?.slug]),
          paste: () => {
            navigator.clipboard.readText().then((clipText) => {
              const el = event.target;
              const children = el.children || [];
              if (children.length > 0) children[0].textContent = clipText.slice(0, 200);
              else el.textContent = clipText.slice(0, 200);
            });
          },
          comment: (val) => this.commentOnCell(field.slug, val),
        });
      },

      openComments() {
        this.openModal({
          component: TenderPositionCard,
          context: {
            attributes: {
              class: ['create-tender__container'],
            },
            row: this.row,
            rowId: this.rowId,
            tender: {
              title: this.listTitle,
            },
          },
        });
      },
      commentOnCell(fieldName, { pageX, pageY }) {
        this.openContext({
          pageX,
          pageY,
          fieldName,
          height: 43,
          width: 231,
          rowId: this.row.id,
          component: CommentInput,
          changeCommentCount: () => {
            this.countComments(1);
            this.commentsResolved = false;
          },
        });
      },
    },
  };
</script>
<style lang="scss">
  @import 'src/styles/components/comments';
</style>
